import depth from '../../utils/depth'
import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  CardActionArea,
  Avatar,
  Badge,
  Chip
} from '@mui/material'
import { SUPPORT_TICKET_STATUS } from '../../utils/enums'

const UserTicketCard = ({ user, isSelected, onClick }) => {
  const theme = useTheme()
  const latestTicket = user.latestTicket

  const hasUnreadMessages = Number(latestTicket?.unreadMessage) > 0
  const badgeColor = hasUnreadMessages
    ? theme.palette.success.main
    : theme.palette.grey[400]

  const getStatusLabel = status => {
    switch (status) {
      case SUPPORT_TICKET_STATUS.OPEN:
        return 'Open'
      case SUPPORT_TICKET_STATUS.IN_PROGRESS:
        return 'In Progress'
      case SUPPORT_TICKET_STATUS.CLOSED:
        return 'Closed'
      default:
        return status
    }
  }

  const getStatusColor = status => {
    switch (status) {
      case SUPPORT_TICKET_STATUS.OPEN:
        return theme.palette.info.main
      case SUPPORT_TICKET_STATUS.IN_PROGRESS:
        return theme.palette.warning.main
      case SUPPORT_TICKET_STATUS.CLOSED:
        return theme.palette.error.main
      default:
        return theme.palette.grey[500]
    }
  }

  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: '14px',
        border: '1.5px solid',
        borderColor: isSelected ? 'primary.main' : 'rgba(16,24,40,0.08)',
        bgcolor: isSelected ? '#FFF6F0' : 'common.white',
        boxShadow: isSelected
          ? '0 10px 22px -12px rgba(244,102,31,0.6)'
          : '0 2px 8px -4px rgba(16,24,40,0.18)',
        transition: 'transform .15s ease, box-shadow .15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isSelected
            ? '0 12px 24px -12px rgba(244,102,31,0.65)'
            : '0 10px 20px -12px rgba(16,24,40,0.3)'
        }
      }}>
      <CardActionArea onClick={onClick}>
        <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: latestTicket ? badgeColor : 'transparent',
                boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
              }
            }}>
            <Avatar
              sx={{
                background: depth.brandGradient,
                boxShadow: depth.brandShadow,
                fontWeight: 700,
                width: 48,
                height: 48,
                fontSize: '1.2rem'
              }}>
              {user.name.charAt(0).toUpperCase()}
            </Avatar>
          </Badge>
          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 0.25
              }}>
              <Typography
                variant="body1"
                fontWeight="600"
                noWrap
                sx={{ color: theme.palette.common.black, minWidth: 0 }}>
                {user.name}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              noWrap
              sx={{ color: theme.palette.text.secondary }}>
              {latestTicket ? latestTicket.title : 'No active tickets'}
            </Typography>
            {latestTicket?.status && (
              <Chip
                label={getStatusLabel(latestTicket.status)}
                size="small"
                sx={{
                  bgcolor: getStatusColor(latestTicket.status),
                  color: theme.palette.common.white,
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  height: 22,
                  flexShrink: 0
                }}
              />
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default UserTicketCard
